import { useChatStore } from '../../../store/chatStore';
import { getMessages } from '../../../api/chat';

/**
 * 검색 결과로 «뛰기» — 그 메시지가 아직 안 불러와졌으면 불러온 뒤에 뛴다
 * (chat-message-search — 2026-09-17).
 *
 * ★ **왜 필요한가.** 서버 검색은 방 «전체»를 찾으므로 결과에 **화면에 없는 메시지**가 들어온다.
 *   종전 `jumpTo` 는 `document.querySelector([data-message-id])` 하나였다 — 없으면 **아무 일도
 *   안 일어난다.** 누르면 창만 닫히고 끝이라, 고장인지 결과가 없는 건지 사용자가 알 수 없다.
 *   「없는 것을 조용히 지나가지 않는다」가 이 파일의 전부다.
 *
 * ★ **왜 커서로 «한 번에» 안 뛰나.** 히트의 commitSeq 로 곧장 그 페이지를 불러오면 목록에
 *   **구멍**이 생긴다 — 최신 N개와 그 언저리 한 뭉치 사이가 비는데, 화면에는 서로 이어진 것처럼
 *   보인다. 그래서 과거 쪽으로 **차례대로** 넘긴다. ChatPage.handleLoadOlder 와 같은 관용구
 *   (cursor → getMessages → appendMessages → setHistoryCursor)를 쓴다.
 */

/** 과거로 넘겨 볼 최대 쪽수 — 못 찾으면 «못 찾았다»고 말한다(무한정 긁지 않는다). */
const MAX_PAGES = 20;
const PAGE = 50;

function hasMessage(roomId: string, messageId: string): boolean {
  const list = useChatStore.getState().messagesByRoom[roomId];
  return Array.isArray(list) && list.some((m) => m.id === messageId);
}

/**
 * 그 메시지가 화면 목록에 들어올 때까지 과거 페이지를 불러온다.
 * @returns 불러왔으면 true. 더 볼 과거가 없거나 상한에 걸리면 false.
 */
export async function ensureMessageLoaded(roomId: string, messageId: string): Promise<boolean> {
  if (hasMessage(roomId, messageId)) return true;

  const store = useChatStore.getState();
  if (store.loadingOlderByRoom[roomId]) return false;
  store.setLoadingOlder(roomId, true);
  try {
    for (let i = 0; i < MAX_PAGES; i++) {
      const cursor = useChatStore.getState().historyCursorByRoom[roomId];
      if (!cursor) return false; // 더 볼 과거가 없다
      const res = await getMessages(roomId, { cursor, limit: PAGE });
      const s = useChatStore.getState();
      s.appendMessages(res.messages);
      s.setHistoryCursor(roomId, res.nextCursor);
      if (hasMessage(roomId, messageId)) return true;
    }
    return false;
  } catch {
    return false; // best-effort — cursor 는 그대로라 사용자가 다시 눌러 재시도할 수 있다
  } finally {
    useChatStore.getState().setLoadingOlder(roomId, false);
  }
}

/**
 * 메시지 행이 **그려질 때까지** 기다린다.
 * appendMessages 직후에는 아직 DOM 이 없다 — 「글자는 떴는데 자리가 아직」인 상태라
 * 곧바로 querySelector 하면 null 이다. rAF 한두 번을 찍기보다 짧게 되물어 확인한다.
 */
export async function waitForMessageEl(messageId: string, tries = 20, gapMs = 50): Promise<Element | null> {
  const sel = `[data-message-id="${CSS.escape(messageId)}"]`;
  for (let i = 0; i < tries; i++) {
    const el = document.querySelector(sel);
    if (el) return el;
    await new Promise((r) => setTimeout(r, gapMs));
  }
  return document.querySelector(sel);
}
